<template>
    <div class="calendar" :id="'datepickerCalendar' + id">
        <span @click="navigate('previous')">
            <IconBase width="5" height="9" iconName="arrowRightIcon"
                      class="previous"
                      :iconColor="consts.COLORS.DARK_300"
                      v-show="navigation === 'previous' || navigation === 'all'"
            />
        </span>
        <span @click="navigate('next')">
            <IconBase width="5" height="9" iconName="arrowRightIcon"
                      class="next"
                      :iconColor="consts.COLORS.DARK_300"
                      v-show="navigation === 'next' || navigation === 'all'"
            />
        </span>
        <div>
            <div class="month-header font-3">
                {{monthName}} {{year}}
            </div>
            <div class="days-header font-6">
                <span v-for="weekDay in dayNames" :key="weekDay">
                    {{weekDay}}
                </span>
            </div>
        </div>
        <div class="days-wrapper font-7">
            <div v-for="(week, weekIndex) in weeks" :key="weekIndex">
                <span v-for="(day, dayIndex) in week"
                      :key="dayIndex"
                      @click="day.disabled || selectDay(day)"
                      class="day"
                      :class="{'selected-day font-6': selectedDaysMap[day.value],
                                'selected-day-between': inBetweenMap[day.value],
                                today: day.isToday,
                                'disabled-day': day.isFuture || day.disabled,
                                'non-day': day.value === 0 }">
                    {{day.value}}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import IconBase from 'components/IconBase.vue';
import consts from 'services/metaData/consts';
import dateServices from './DateServices';

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default {
    name: 'calendarMonth',
    components: { IconBase },
    props: {
        year: {
            type: Number,
            required: true
        },
        days: {
            type: Object,
            required: true
        },
        weeks: {
            type: Array,
            required: true
        },
        month: Number,
        navigation: {
            type: String,
            validator: (val) => ['next', 'previous', 'all'].includes(val)
        },
        range: {
            type: Object,
            validator: (val) => val.fromStart !== undefined && val.toEnd !== undefined
        },
        type: {
            type: String,
            validator: (val) => ['date', 'week', 'month'].includes(val)
        },
        id: String,
        allowedDays: {
            type: Number,
            default: null
        }
    },
    watch: {
        month() {
            this.updateSelection();
        },
        range(newVal, oldVal) {
            if (newVal && newVal !== oldVal) {
                this.updateSelection();
            }
        },
        type(newVal, oldVal) {
            if (newVal && newVal !== oldVal) {
                this.setMinDateInMonth(true);
            }
        }
    },
    data() {
        return {
            dayNames: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
            selectedDays: [],
            consts,
            selectedDaysMap: {},
            inBetweenMap: {}
        };
    },
    computed: {
        monthName() {
            return months[this.month];
        }
    },
    methods: {
        selectDay(day) {
            if (day.value === 0) {
                return;
            }

            this.$emit('selectionChanged', day, this.month, this.year);
        },
        navigate(navigation) {
            this.$emit('navigation', navigation);
        },
        isMonthInRange() {
            if (!this.range.fromStart || !this.range.toEnd) {
                return false;
            }

            const fromMonth = this.range.fromStart.getMonth();
            const fromYear = this.range.fromStart.getFullYear();
            const toMonth = this.range.toEnd.getMonth();
            const toYear = this.range.toEnd.getFullYear();
            if (this.year < fromYear || this.year > toYear) {
                return false;
            }
            return !((this.year === fromYear && this.month < fromMonth)
                || (this.year === toYear && this.month > toMonth));
        },
        getFirstDayInRangeForMonth(date, month) {
            if ((date.getMonth() < month.month && date.getFullYear() === month.year)
                || (date.getFullYear() < month.year)) {
                return 1;
            }
            if (date.getMonth() === month.month && date.getFullYear() === month.year) {
                return date.getDate();
            }
            return dateServices.daysInMonth(month.month, month.year);
        },
        getLastDayInRangeForMonth(date, month) {
            if ((date.getMonth() > month.month && date.getFullYear() === month.year)
                || (date.getFullYear() > month.year)) {
                return dateServices.daysInMonth(month.month, month.year);
            }
            if (date.getMonth() === month.month && date.getFullYear() === month.year) {
                return date.getDate();
            }
            return 1;
        },
        getMinDateInMonth() {
            if (this.allowedDays) {
                const today = dateServices.getCurrentTimeUTC();
                today.setDate(today.getDate() - this.allowedDays + 1);
                let minDate = today;
                switch (this.type) {
                case 'week':
                    minDate = dateServices.getFirstDayOfWeek(today);
                    break;
                case 'month':
                    minDate.setDate(1);
                    break;
                default:
                    break;
                }

                minDate.setHours(0, 0, 0, 0);
                return minDate;
            }
            return null;
        },
        setMinDateInMonth(resetMonth) {
            const minDate = this.getMinDateInMonth();
            if (!minDate && resetMonth) {
                Object.keys(this.days).forEach((dayKey) => {
                    const day = this.days[dayKey];
                    this.$set(day, 'disabled', false);
                });
            } else if (minDate && (this.year <= minDate.getFullYear()
                    || (this.year === minDate.getFullYear() && this.month <= minDate.getMonth()))) {
                // go over the days if the month is below or equal to the min date
                Object.keys(this.days).forEach((dayKey) => {
                    const day = this.days[dayKey];
                    this.$set(day, 'disabled', day.date < minDate);
                });
            }
        },
        setSelectionInMonth(date, day, lowerLimit, upperLimit) {
            if (this.year === date.getFullYear() && this.month === date.getMonth()
                && day >= lowerLimit && day <= upperLimit) {
                this.$set(this.selectedDaysMap, day, true);
            }
        },
        updateSelection() {
            for (let i = 0; i < this.selectedDays.length; i++) {
                const day = this.selectedDays[i];
                this.$delete(this.selectedDaysMap, day.value);
                this.$delete(this.inBetweenMap, day.value);
            }
            this.selectedDays = [];

            if (this.isMonthInRange()) {
                const thisMonth = { month: this.month, year: this.year };
                const selectionFromStart = this.getFirstDayInRangeForMonth(this.range.fromStart, thisMonth);
                const selectionFromEnd = this.getLastDayInRangeForMonth(this.range.fromEnd, thisMonth);
                const selectionToStart = this.getFirstDayInRangeForMonth(this.range.toStart, thisMonth);
                const selectionToEnd = this.getLastDayInRangeForMonth(this.range.toEnd, thisMonth);

                for (let j = selectionFromStart; j <= selectionToEnd; j++) {
                    this.$set(this.inBetweenMap, j, true);

                    // if the selection from start is in the current month
                    this.setSelectionInMonth(this.range.fromStart, j, selectionFromStart, selectionFromEnd);
                    // if the selection from end is in the current month
                    this.setSelectionInMonth(this.range.fromEnd, j, selectionFromStart, selectionFromEnd);
                    // if the selection to start is in the current month
                    this.setSelectionInMonth(this.range.toStart, j, selectionToStart, selectionToEnd);
                    // if the selection to start is in the current month
                    this.setSelectionInMonth(this.range.toEnd, j, selectionToStart, selectionToEnd);

                    this.selectedDays.push(this.days[j]);
                }
            }
        }
    },
    created() {
        this.setMinDateInMonth();
        this.updateSelection();
    }
};
</script>

<style scoped lang="scss">
    @import "~styles/styleGuide";
    $calendarWidth: 674px;
    $menuWidth: 249px;
    $hoverDayBackgound: darken($primary-100, 10%);
    $singleDayWidth: 28px;
    $singleDayHeight: 28px;
    $daterangeHeightField: 32px;
    $daterangeItemHeight: 40px;
    $dateRangeDropdownPadding: 20px;

    @mixin dayHover{
        &:hover {
            background-color: $hoverDayBackgound;
        }
    }
    .days-wrapper {
        border-top: $border;

        .day {
            width: $singleDayWidth;
            height: $singleDayHeight;
            cursor: pointer;
            display: inline-block;
            text-align: center;
            padding: 5px;
            line-height: 19px;
            box-sizing: border-box;
            @include dayHover;

            &.today {
                text-decoration: underline;
            }

            &.selected-day-between {
                background-color: $primary-100;
                font-weight: bold;

                &:hover {
                    background-color: $hoverDayBackgound;
                }
            }

            &.selected-day {
                background-color: $primaryColor !important;
                color: $white;
            }

            &.disabled-day {
                color: $light-700;
                cursor: default;
                background: transparent;
            }

            &.non-day {
                color: transparent;
                cursor: default;
                background: transparent;
            }
        }
    }

    @mixin calendarArrow{
        cursor: pointer;
        position: absolute;
        z-index: 16;
    }

    .calendar {
        box-sizing: unset;
        padding: 0 11px;
        overflow: hidden;
        width: ($singleDayWidth * 7); // day width*7
        user-select: none;
        .days-header{
            padding: 10px 0 6px 0;
            span {
                width: $singleDayWidth;
                display: inline-block;
                text-align: center;
            }
        }

        .month-header{
            padding-top: 17px;
            padding-bottom: 6px;
            text-align: center;
        }
        .next{
            right: 26px;
            margin-top: 10px;
            @include calendarArrow;
        }
        .previous{
            left: 29px;
            margin-top: 16px;
            transform: rotate(180deg);
            @include calendarArrow;
        }
    }
</style>
